/**
 * ContentRestoreBanner — "Restoring your pages…" after a reactivation.
 *
 * When a lapsed subscription comes back, the backend brings the hidden
 * Autonoma products / pages / theme assets back in a detached, retried
 * job (content-restore.service). Its state rides on
 * GET /api/billing/access-state as `contentRestore`:
 *   running → this banner (info) with progress, polling every 10s;
 *   done    → the banner disappears;
 *   failed  → a warning (support has been alerted) the merchant can dismiss.
 * Dismissal is per run (sessionStorage keyed by startedAt) so the same
 * run never nags again, while the next reactivation shows it afresh.
 */
import React, { useEffect, useState } from 'react';
import { Banner } from '@shopify/polaris';

const POLL_MS = 10 * 1000;
const DISMISS_KEY = 'au-content-restore-dismissed';

function readDismissed() {
  try { return window.sessionStorage.getItem(DISMISS_KEY); } catch { return null; }
}

export default function ContentRestoreBanner({ contentRestore, reload }) {
  const status = contentRestore?.status || null;
  const startedAt = contentRestore?.startedAt || null;
  const [dismissedFor, setDismissedFor] = useState(readDismissed);

  // Keep the state fresh while the restore is running.
  useEffect(() => {
    if (status !== 'running' || typeof reload !== 'function') return undefined;
    const t = setInterval(() => reload(), POLL_MS);
    return () => clearInterval(t);
  }, [status, reload]);

  if (!status || (status !== 'running' && status !== 'failed')) return null;
  if (dismissedFor && dismissedFor === startedAt) return null;

  const dismiss = () => {
    setDismissedFor(startedAt);
    try { window.sessionStorage.setItem(DISMISS_KEY, startedAt || '1'); } catch { /* private mode */ }
  };

  if (status === 'failed') {
    return (
      <div style={{ padding: '12px 16px 0' }} data-testid="content-restore-banner" data-status="failed">
        <Banner title="Some of your pages could not be restored automatically" tone="warning" onDismiss={dismiss}>
          <p>
            Your subscription is active, but {contentRestore.failed || 'some'} item{contentRestore.failed === 1 ? '' : 's'} did not come back on
            their own. We have been alerted and keep retrying — if anything is still missing tomorrow, contact support from Settings.
          </p>
        </Banner>
      </div>
    );
  }

  const p = contentRestore.progress;
  const progressText = p && p.total > 0 ? ` (${Math.min(p.restored, p.total)} of ${p.total} back so far)` : '';
  const retrying = contentRestore.attempt > 1 ? ` Attempt ${contentRestore.attempt} of ${contentRestore.maxAttempts}.` : '';
  return (
    <div style={{ padding: '12px 16px 0' }} data-testid="content-restore-banner" data-status="running">
      <Banner title="Restoring your pages…" tone="info" onDismiss={dismiss}>
        <p>
          Your subscription is active again. We are bringing your Autonoma products and pages back online{progressText} —
          this usually takes under a minute, and you can keep working meanwhile.{retrying}
        </p>
      </Banner>
    </div>
  );
}
